// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { responseMetadataSchema } from '@/api/types';
import { DateTime } from 'luxon';
import { z } from 'zod';

// A facet set to all is stored as a mode rather than as the keys that existed
// when the filter was saved, so an option created later is part of the answer.
export const membershipFacetSchema = z.object({
  isAll: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
  keys: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
});
export type MembershipFacet = z.infer<typeof membershipFacetSchema>;

// A unit joins the population by satisfying any inclusion, so turning on more of
// them widens the answer. An exclusion then decides which of its days count.
export const membershipJoinRuleKindSchema = z.enum([
  'DELIVERED_TO_LOCATION',
  'FULFILLED_IN_WINDOW',
]);
export type MembershipJoinRuleKind = z.infer<
  typeof membershipJoinRuleKindSchema
>;

export const membershipLeaveRuleKindSchema = z.enum(['LEFT_PROJECT_LOCATION']);
export type MembershipLeaveRuleKind = z.infer<
  typeof membershipLeaveRuleKindSchema
>;

export const membershipJoinRuleSchema = z.object({
  kind: membershipJoinRuleKindSchema,
  facet: membershipFacetSchema,
});

export const membershipLeaveRuleSchema = z.object({
  kind: membershipLeaveRuleKindSchema,
  facet: membershipFacetSchema,
});

export const membershipScopeSchema = z.object({
  items: membershipFacetSchema,
  joinRules: z
    .array(membershipJoinRuleSchema)
    .nullish()
    .transform((v) => v ?? []),
  leaveRules: z
    .array(membershipLeaveRuleSchema)
    .nullish()
    .transform((v) => v ?? []),
});
export type MembershipScope = z.infer<typeof membershipScopeSchema>;

export const membershipSelectionSchema = z.object({
  id: z.string(),
  scope: membershipScopeSchema,
});

export const membershipRulesDefinitionSchema = z.object({
  version: z.number(),
  selections: z.array(membershipSelectionSchema),
});
export type MembershipRulesDefinition = z.infer<
  typeof membershipRulesDefinitionSchema
>;

// A project holds one membership report. isConfigured is false when nobody has
// saved one, in which case the definition is the default the project answers
// with rather than an empty form.
export const membershipRulesSchema = z.object({
  projectKey: z.string(),
  isConfigured: z.boolean(),
  version: z.number(),
  definition: membershipRulesDefinitionSchema,
});
export type MembershipRules = z.infer<typeof membershipRulesSchema>;

// One unbroken stretch a unit spent as a member, at one site. The range is half
// open, so a membership still open carries no leftOn and no leftVia.
// Where an asset stood from fromDay until the next entry. An asset that never
// moved carries one.
export const membershipLocationSchema = z.object({
  fromDay: z.number(),
  name: z.string(),
});
export type MembershipLocation = z.infer<typeof membershipLocationSchema>;

// Residency is one bit per day of the window, packed and base64 encoded. See
// ai/context/backend/presence-bit-mask.md for the encoding. It is decoded off
// the main thread, so this keeps it a string.
export const membershipAssetSchema = z.object({
  key: z.string(),
  serialNumber: z.string(),
  itemSku: z.string(),
  residency: z.string(),
  locations: z
    .array(membershipLocationSchema)
    .nullish()
    .transform((v) => v ?? []),
});

// The series the charts draw, reduced by the server. counts has one entry per
// day; joined and left are keyed by day because most days move nothing.
export const membershipSkuSchema = z.object({
  itemKey: z.string(),
  itemSku: z.string(),
  counts: z
    .array(z.number())
    .nullish()
    .transform((v) => v ?? []),
  joined: z
    .record(z.string(), z.number())
    .nullish()
    .transform((v) => v ?? {}),
  left: z
    .record(z.string(), z.number())
    .nullish()
    .transform((v) => v ?? {}),
});
export type MembershipSku = z.infer<typeof membershipSkuSchema>;

// The shape validated before any date is rehydrated, so the decode reads plain
// strings and builds one DateTime rather than one per membership.
export const membershipReportPayloadSchema = z.object({
  metadata: responseMetadataSchema,
  data: z.object({
    evaluatedAt: z.string(),
    startOn: z.string(),
    dayCount: z.number(),
    skus: z
      .array(membershipSkuSchema)
      .nullish()
      .transform((v) => v ?? []),
    assets: z
      .array(membershipAssetSchema)
      .nullish()
      .transform((v) => v ?? []),
  }),
});

/** A report as it arrives, before the masks are unpacked. */
export type MembershipReportPayload = z.infer<
  typeof membershipReportPayloadSchema
>['data'];

/** One member, without the mask that says which days it counted. */
export interface MembershipAssetInfo {
  key: string;
  serialNumber: string;
  itemSku: string;
  locations: MembershipLocation[];
}

/**
 * A decoded report. The masks arrive as one buffer with a fixed stride rather
 * than an array per asset, so a project with thousands of them is one
 * allocation.
 */
export interface MembershipReportData {
  evaluatedAt: string;
  startOn: DateTime;
  dayCount: number;
  skus: MembershipSku[];
  assets: MembershipAssetInfo[];
  residency: Uint8Array;
  bytesPerMask: number;
}

export const membershipScopeOptionSchema = z.object({
  key: z.string(),
  label: z.string(),
});
export type MembershipScopeOption = z.infer<typeof membershipScopeOptionSchema>;

// The list is also the default for the facet, so an unedited rule set selects
// the whole project.
export const membershipScopeOptionsSchema = z.object({
  items: z
    .array(membershipScopeOptionSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type MembershipScopeOptions = z.infer<
  typeof membershipScopeOptionsSchema
>;
